import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {ContactCardPanelId} from '../../../../libs/shared/core/src/store/panels/panelStateBase';
import {Skeleton, SkeletonColorSchemesEnum} from '../../../../libs/shared/ui/src/components/utility/skeleton';
import {AccountModel} from '../../../models/api/accountModel';
import {CardModel} from '../../../models/api/cardModel';
import {VisualContactModel} from '../../../models/api/visualContactModel';
import {ContactCardLocationsEnum} from '../../../store/panels/contacts/contactCard/contactCardPanelState';
import {ContactCardAccountDetails} from './contactCardAccountDetails';
import {ContactCardCardDetailsContainer} from './contactCardCardDetailsContainer';
import {ContactCardNoCardDetails} from './contactCardNoCardDetails';
import {ContactCardSection} from './contactCardSection';

/*
 * Props.
 */

interface ContactCardDetailsProps {
  panelId: ContactCardPanelId;
  visualContact: VisualContactModel;
  card?: CardModel;
  account?: AccountModel;
  isLoading: boolean;
  isUserVisitor: boolean;
  location: ContactCardLocationsEnum;
}

/*
 * Style.
 */

const StyledLoadingWrapperDiv = styled.div`
  display: flex;
  flex-flow: column;
  gap: 8px;
`;

const StyledTitleSkeleton = styled(Skeleton)`
  margin-bottom: 4px;
`;

/*
 * Component.
 */

export const ContactCardDetails: FC<ContactCardDetailsProps> = (props) => {
  const {panelId, card, account, isLoading, visualContact, isUserVisitor, location} = props;

  if (isLoading) {
    return renderLoadingSection();
  }
  // If the user is a visitor, we can just rely on the visual contact instead of card.
  if (!card || isUserVisitor) {
    return renderNoCardSection(visualContact);
  }

  return (
    <>
      <ContactCardSection title={maybeRenderContactDetailsTitle(account)} shouldHideTopBorder>
        <ContactCardCardDetailsContainer panelId={panelId} card={card} location={location} />
      </ContactCardSection>
      {maybeRenderAccountSection(props)}
    </>
  );
};

/*
 * Helpers.
 */

function maybeRenderContactDetailsTitle(account?: AccountModel) {
  if (!account) {
    return undefined;
  }
  return <Trans id="contact-card-contact-details-section-title">Contact details</Trans>;
}

function renderLoadingSection() {
  return (
    <ContactCardSection>
      <StyledLoadingWrapperDiv>
        <StyledTitleSkeleton height="16px" width="153px" />
        <Skeleton height="16px" width="120px" scheme={SkeletonColorSchemesEnum.GREY10} />
        <Skeleton height="16px" width="238px" scheme={SkeletonColorSchemesEnum.GREY10} />
        <Skeleton height="16px" width="188px" scheme={SkeletonColorSchemesEnum.GREY10} />
      </StyledLoadingWrapperDiv>
    </ContactCardSection>
  );
}

/**
 * This can occur when we only have a visual contact only.
 */
function renderNoCardSection(visualContact: VisualContactModel) {
  return (
    <ContactCardSection>
      <ContactCardNoCardDetails visualContact={visualContact} />
    </ContactCardSection>
  );
}

function maybeRenderAccountSection(props: ContactCardDetailsProps) {
  const {panelId, card, account} = props;
  if (!account || !card) {
    return null;
  }

  // If the accounts have no details to render, do not render anything.
  if (account.domains.length === 0 && !account.description && account.customFieldAttributes?.length === 0) {
    return null;
  }

  return (
    <ContactCardSection
      title={<Trans id="contact-card-account-details-section-title">Account details</Trans>}
    >
      <ContactCardAccountDetails panelId={panelId} account={account} card={card} />
    </ContactCardSection>
  );
}
